import { useState } from 'react';
import { Loader2, Video } from 'lucide-react';
import type { AcademyCreatorItem } from '../types';
import { useInsertVideo } from '../hooks/useAcademyAdmin';
const inputClass = "w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary/50 transition-colors";
const labelClass = "text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1 block";

interface AcademyCreateVideoFormProps {
    creators: AcademyCreatorItem[];
    onCreated: () => void;
    onCancel: () => void;
}

const YOUTUBE_URL_REGEX = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+/i;
function isValidYouTubeUrl(url: string): boolean {
    return YOUTUBE_URL_REGEX.test(url.trim());
}

function getYouTubeThumbnail(url: string): string {
    const m = url.match(/(?:v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    if (m) return `https://img.youtube.com/vi/${m[1]}/mqdefault.jpg`;
    return '';
}

export function AcademyCreateVideoForm({ creators, onCreated, onCancel }: AcademyCreateVideoFormProps) {
    const [creatorId, setCreatorId] = useState('');
    const [title, setTitle] = useState('');
    const [videoUrl, setVideoUrl] = useState('');
    const [description, setDescription] = useState('');
    const [waterType, setWaterType] = useState('');
    const insertMutation = useInsertVideo();

    const approvedCreators = creators.filter(c => c.status === 'approved');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!creatorId || !title.trim()) return;
        if (videoUrl.trim() && !isValidYouTubeUrl(videoUrl)) return;

        const thumbnailUrl = videoUrl.trim() ? getYouTubeThumbnail(videoUrl) : null;
        try {
            await insertMutation.mutateAsync({
                p_creator_id: creatorId,
                p_title: title.trim(),
                p_description: description.trim() || null,
                p_video_url: videoUrl.trim() || null,
                p_thumbnail_url: thumbnailUrl,
                p_water_type: waterType || null,
            });
            onCreated();
        } catch {
            // Error handled by mutation
        }
    };

    if (approvedCreators.length === 0) {
        return (
            <div className="rounded-xl border border-dashed border-border bg-card/50 p-8 text-center">
                <Video size={32} className="mx-auto text-muted-foreground/40 mb-3" />
                <p className="text-sm text-muted-foreground">
                    No hay creadores aprobados. Aprueba al menos un creador para poder añadir vídeos.
                </p>
                <button
                    onClick={onCancel}
                    className="mt-4 px-4 py-2 rounded-lg text-sm font-medium bg-muted/50 text-muted-foreground hover:bg-muted"
                >
                    Cerrar
                </button>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="rounded-xl border border-border bg-card p-6 space-y-4">
            <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Video size={16} />
                Añadir Vídeo Manual
            </h3>

            <div>
                <label className={labelClass}>Creador *</label>
                <select
                    value={creatorId}
                    onChange={e => setCreatorId(e.target.value)}
                    className={inputClass}
                    required
                >
                    <option value="">Selecciona un creador</option>
                    {approvedCreators.map(c => (
                        <option key={c.id} value={c.id}>
                            {c.user_display_name || c.user_email || c.youtube_channel_url}
                        </option>
                    ))}
                </select>
            </div>

            <div>
                <label className={labelClass}>URL de YouTube</label>
                <input
                    type="url"
                    value={videoUrl}
                    onChange={e => setVideoUrl(e.target.value)}
                    placeholder="https://www.youtube.com/watch?v=..."
                    className={inputClass}
                />
            </div>

            <div>
                <label className={labelClass}>Título *</label>
                <input
                    type="text"
                    value={title}
                    onChange={e => setTitle(e.target.value)}
                    placeholder="Título del vídeo"
                    className={inputClass}
                    required
                />
            </div>

            <div>
                <label className={labelClass}>Descripción</label>
                <textarea
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    placeholder="Descripción opcional"
                    className={inputClass}
                    rows={2}
                />
            </div>

            <div>
                <label className={labelClass}>Tipo de agua</label>
                <select
                    value={waterType}
                    onChange={e => setWaterType(e.target.value)}
                    className={inputClass}
                >
                    <option value="">—</option>
                    <option value="dulce">Dulce</option>
                    <option value="salada">Salada</option>
                </select>
            </div>

            <div className="flex gap-2 pt-2">
                <button
                    type="button"
                    onClick={onCancel}
                    className="px-4 py-2 rounded-lg text-sm font-medium bg-muted/50 text-muted-foreground hover:bg-muted"
                >
                    Cancelar
                </button>
                <button
                    type="submit"
                    disabled={insertMutation.isPending || !creatorId || !title.trim()}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                    {insertMutation.isPending && <Loader2 size={14} className="animate-spin" />}
                    Añadir Vídeo
                </button>
            </div>
        </form>
    );
}
